import { useEffect, useState } from 'react'
import './adamLives.css'

/**
 * Bulele lui Adam: cate una pe fiecare Adam al omului care lucreaza (sau abia a
 * terminat). Titlul scurt, pasul de acum intr-o propozitie, starea prin
 * culoare - lucreaza (albastru), asteapta aprobare (galben), gata (verde),
 * eroare (rosu). Clic = conversatia aceea in fereastra.
 *
 * Coborat, bulele stau pe bara de jos, la stanga lui Adam (`--od-ai-foot`,
 * `--od-ai-perch-right`, masurate de `fitAboveBar`). Ridicat, se deseneaza in
 * sertar, ca file intre conversatiile in lucru (`inside`).
 *
 * "Gata" dispare singura dupa cateva secunde (sertarul o scoate); aprobarea si
 * eroarea raman pana le inchide omul, fiindca cer ceva de la el.
 *
 * Sageata din dreapta o EXTINDE: ultimii pasi ai rularii (`trail`), starea,
 * timpul scurs si butoanele Open / Open beside / Stop / Close. Restransa e ca
 * inainte. "Open beside" apare numai cand sertarul a dat `onBeside` (mai multi
 * Adam si loc pe ecran pentru un panou in stanga lui).
 */

export type AdamLiveState = 'working' | 'approval' | 'done' | 'error'

export interface AdamLiveBubble {
  key: number
  convId: number
  title: string
  step: string
  state: AdamLiveState
  /** ultimii pasi ai rularii, cel mai nou la urma (cel mult TRAIL_MAX) */
  trail?: string[]
  /** cand a pornit (ms) - pentru timpul scurs din bula extinsa */
  since?: number
}

/** Cati pasi tine bula extinsa. */
export const TRAIL_MAX = 5

export function pushTrail(trail: string[] | undefined, step: string): string[] {
  const cur = trail ?? []
  if (step === '' || cur[cur.length - 1] === step) return cur
  return [...cur, step].slice(-TRAIL_MAX)
}

export function liveElapsed(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}

export const LIVE_STATE_LABEL: Record<AdamLiveState, string> = {
  working: 'working',
  approval: 'waiting for your approval',
  done: 'done',
  error: 'error',
}

/** Bula "gata" sta atat pe ecran inainte sa dispara. */
export const BUBBLE_LINGER = 6000

export function shortTitle(text: string, max = 40): string {
  const one = text.replace(/\s+/g, ' ').trim()
  return one.length > max ? one.slice(0, max - 1).trimEnd() + '…' : one
}

export function AdamLives({
  lives,
  current,
  inside = false,
  onOpen,
  onClose,
  onStop,
  onBeside,
}: {
  lives: AdamLiveBubble[]
  /** conversatia din fereastra - bula ei e aprinsa cand sertarul e sus */
  current?: number
  inside?: boolean
  onOpen(live: AdamLiveBubble): void
  onClose(live: AdamLiveBubble): void
  /** opreste rularea (aceeasi oprire ca butonul din fir) */
  onStop?(live: AdamLiveBubble): void
  /** deschide conversatia intr-un panou in stanga sertarului */
  onBeside?(live: AdamLiveBubble): void
}) {
  const [expanded, setExpanded] = useState<number[]>([])
  const [, setTick] = useState(0)
  const anyOpenWorking = lives.some((l) => l.state === 'working' && expanded.includes(l.key))
  useEffect(() => {
    if (!anyOpenWorking) return
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [anyOpenWorking])
  if (lives.length === 0) return null
  const toggle = (key: number) => setExpanded((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]))
  return (
    <div className={`od-ai-lives${inside ? ' is-inside' : ''}`} id={inside ? 'odAiLivesIn' : 'odAiLives'} role="list" aria-label="Adam runs">
      {lives.map((live) => {
        const open = expanded.includes(live.key)
        const trail = live.trail ?? []
        return (
          <div
            key={live.key}
            role="listitem"
            className={`od-ai-live is-${live.state}${inside && current === live.convId ? ' is-current' : ''}${open ? ' is-expanded' : ''}`}
            data-conv={live.convId}
            title={open ? undefined : `${live.title} - ${LIVE_STATE_LABEL[live.state]}`}
          >
            <div className="od-ai-live-line">
              <button type="button" className="od-ai-live-open" onClick={() => onOpen(live)}>
                <span className="od-ai-live-dot" aria-hidden="true"></span>
                <span className="od-ai-live-text">
                  <span className="od-ai-live-title">{live.title || 'Adam'}</span>
                  <span className="od-ai-live-step">{live.state === 'working' ? live.step : LIVE_STATE_LABEL[live.state]}</span>
                </span>
              </button>
              <button
                type="button"
                className="od-ai-live-expand"
                aria-expanded={open}
                aria-label={open ? 'Collapse this bubble' : 'Expand this bubble'}
                title={open ? 'Collapse' : 'Expand'}
                onClick={() => toggle(live.key)}
              >
                <i className={`fas fa-chevron-${open ? 'down' : 'up'}`} aria-hidden="true"></i>
              </button>
              {live.state !== 'working' && !open ? (
                <button type="button" className="od-ai-live-close" aria-label="Close this bubble" title="Close" onClick={() => onClose(live)}>
                  &times;
                </button>
              ) : null}
            </div>
            {open ? (
              <div className="od-ai-live-more">
                <div className="od-ai-live-meta">
                  <span className="od-ai-live-state">{LIVE_STATE_LABEL[live.state]}</span>
                  {live.since ? <span className="od-ai-live-time">{liveElapsed(Date.now() - live.since)}</span> : null}
                </div>
                {trail.length > 0 ? (
                  <ol className="od-ai-live-trail">
                    {trail.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ol>
                ) : (
                  <div className="od-ai-live-trail is-empty">no steps yet</div>
                )}
                <div className="od-ai-live-acts">
                  <button type="button" className="od-ai-live-act" data-act="open" onClick={() => onOpen(live)}>
                    Open
                  </button>
                  {onBeside && live.convId > 0 ? (
                    <button
                      type="button"
                      className="od-ai-live-act"
                      data-act="beside"
                      title="Open in a second panel, left of the drawer"
                      onClick={() => onBeside(live)}
                    >
                      Open beside
                    </button>
                  ) : null}
                  {onStop && live.state === 'working' ? (
                    <button type="button" className="od-ai-live-act is-stop" data-act="stop" onClick={() => onStop(live)}>
                      Stop
                    </button>
                  ) : null}
                  <button type="button" className="od-ai-live-act" data-act="close" onClick={() => onClose(live)}>
                    Close
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
